// src/hooks/useBriefingProjects.ts
import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  getBriefingProjects,
  getBriefingProject,
  createBriefingProject,
  updateBriefingProject,
  deleteBriefingProject,
  type BriefingProject,
} from "../api/briefing";
import type {
  CreateBriefingProjectData,
  UpdateBriefingProjectData,
  BriefingDashboardStats,
  UseBriefingProjectsReturn,
} from "../types/briefing";
import { dispatchTokenExpiredEvent } from "../utils/authEvents";

import { logger } from "../utils/logger";

export function useBriefingProjects(): UseBriefingProjectsReturn {
  const [projects, setProjects] = useState<BriefingProject[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const fetchingRef = useRef(false);

  const { user } = useAuth();

  const handleTokenExpired = useCallback(() => {
    logger.warn(
      "Token expired in briefing projects hook, dispatching global event",
    );
    dispatchTokenExpiredEvent();
  }, []);

  // Check authentication status
  useEffect(() => {
    setIsAuthenticated(!!user?.id);
  }, [user]);

  // Fetch projects
  const fetchProjects = useCallback(async () => {
    if (!user?.id) {
      setIsAuthenticated(false);
      setProjects([]);
      return;
    }

    // Prevent duplicate calls if already fetching
    if (fetchingRef.current) {
      return;
    }

    fetchingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const fetchedProjects = await getBriefingProjects(
        user,
        handleTokenExpired,
      );
      setProjects(fetchedProjects);
      setIsAuthenticated(true);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to fetch briefing projects";
      setError(errorMessage);
      logger.error("Failed to fetch briefing projects:", err);

      // If authentication error, clear projects
      if (
        errorMessage.includes("Authentication") ||
        errorMessage.includes("token")
      ) {
        setIsAuthenticated(false);
        setProjects([]);
      }
    } finally {
      fetchingRef.current = false;
      setIsLoading(false);
    }
  }, [user, handleTokenExpired]);

  // Initial load
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Create new project
  const createProject = useCallback(
    async (data: CreateBriefingProjectData): Promise<BriefingProject> => {
      if (!user?.id) {
        throw new Error("Authentication required");
      }

      setError(null);

      try {
        const projectPayload = { ...data };
        delete projectPayload.project_knowledge_files;
        const newProject = await createBriefingProject(
          projectPayload,
          user,
          handleTokenExpired,
        );
        setProjects((prev) =>
          prev.some((project) => project.id === newProject.id)
            ? prev
            : [...prev, newProject],
        );
        return newProject;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to create project";
        setError(errorMessage);
        throw err;
      }
    },
    [user, handleTokenExpired],
  );

  // Update existing project
  const updateProject = useCallback(
    async (
      projectId: string,
      data: UpdateBriefingProjectData,
    ): Promise<BriefingProject> => {
      if (!user?.id) {
        throw new Error("Authentication required");
      }

      setError(null);

      try {
        const updatedProject = await updateBriefingProject(
          projectId,
          data,
          user,
          handleTokenExpired,
        );
        setProjects((prev) =>
          prev.map((project) =>
            project.id === projectId ? updatedProject : project,
          ),
        );
        return updatedProject;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to update project";
        setError(errorMessage);
        throw err;
      }
    },
    [user, handleTokenExpired],
  );

  // Delete project
  const deleteProject = useCallback(
    async (projectId: string): Promise<void> => {
      if (!user?.id) {
        throw new Error("Authentication required");
      }

      setError(null);

      try {
        await deleteBriefingProject(projectId, user, handleTokenExpired);
        setProjects((prev) =>
          prev.filter((project) => project.id !== projectId),
        );
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to delete project";
        setError(errorMessage);
        throw err;
      }
    },
    [user, handleTokenExpired],
  );

  // Refresh projects
  const refreshProjects = useCallback(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Get single project with full details (including default params)
  const getProject = useCallback(
    async (projectId: string): Promise<BriefingProject> => {
      if (!user?.id) {
        throw new Error("Authentication required");
      }

      try {
        const project = await getBriefingProject(
          projectId,
          user,
          handleTokenExpired,
        );
        // Update the project in the local state as well
        setProjects((prev) =>
          prev.map((p) => (p.id === projectId ? project : p)),
        );
        return project;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch project";
        logger.error("Failed to fetch project:", err);
        throw new Error(errorMessage);
      }
    },
    [user, handleTokenExpired],
  );

  // Calculate stats
  const stats: BriefingDashboardStats = {
    totalProjects: projects.length,
    totalAnalyses: 0, // Would need to aggregate from all projects
    completedAnalyses: 0, // Would need to aggregate from all projects
    failedAnalyses: 0, // Would need to aggregate from all projects
  };

  return {
    projects,
    isLoading,
    error,
    createProject,
    updateProject,
    deleteProject,
    getProject,
    refreshProjects,
    isAuthenticated,
    stats,
  };
}
